/**
 * Root application component.
 * Sets up React Router, the Axios 401 interceptor, and renders the appropriate page.
 */

import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Login from './pages/Login';
import Tracker from './pages/Tracker';
import TourDemoRoute from './tour/TourDemoRoute';
import { useAppStore } from './store/appStore';

/**
 * Inner component that registers the Axios 401 interceptor and applies the
 * font-size preference to `<html>`. Must be rendered inside BrowserRouter.
 */
function AppRoutes() {
  const logout   = useAppStore((s) => s.logout);
  const fontSize = useAppStore((s) => s.prefs.fontSize);

  // Apply font-size preference to html element so all rem values scale with it.
  useEffect(() => {
    document.documentElement.style.fontSize = `${fontSize}px`;
  }, [fontSize]);

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (res) => res,
      (error) => {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          logout();
          window.location.replace('/tracker/login');
        }
        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, [logout]);

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/tour" element={<TourDemoRoute />} />
      <Route path="/" element={<Tracker />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * Application root — wraps everything in BrowserRouter.
 * The base path is `/tracker` in production (FastAPI serves at /tracker/).
 */
export default function App() {
  return (
    <BrowserRouter basename="/tracker">
      <AppRoutes />
    </BrowserRouter>
  );
}
